import { useMarketplace } from 'hooks';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import styles from './PeriodSelectorSection.module.scss';
import clsx from 'clsx';
import DateSelectorComponent from 'components/CreateBooking/DateSelector/DateSelectorSection/DateSelectorComponent';
import CalendarBooking from 'components/CreateBooking/DateSelector/DateSelectorSection/CalendarBooking';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon } from '@fortawesome/pro-solid-svg-icons';

interface PeriodSelectorSectionProps {
    from: Moment;
    to: Moment;
    labelTimeStart?: string;
    labelTimeEnd?: string;
    readOnly?: boolean;
    className?: string;
    onSelectPeriod: (from: Moment, to: Moment, nights: number) => void;
}

export const PeriodSelectorSection: FunctionComponent<PeriodSelectorSectionProps> = props => {
    const marketplace = useMarketplace();

    const [expanded, setExpanded] = useState({
        checkIn: false,
        checkOut: false
    });

    const ref = useRef<HTMLDivElement>(null);

    const expandCheckInHandler = () => {
        setExpanded(prev => ({
            ...prev,
            checkIn: !prev.checkIn,
            checkOut: false
        }));
    };

    const closeCheckInHandler = () => {
        setExpanded(prev => ({ ...prev, checkIn: false }));
    };

    const expandCheckOutHandler = () => {
        setExpanded(prev => ({
            ...prev,
            checkIn: false,
            checkOut: !prev.checkOut
        }));
    };

    const closeCheckOutHandler = () => {
        setExpanded(prev => ({ ...prev, checkOut: false }));
    };

    const selectCheckInHandler = (selectedDate: Date) => {
        closeCheckInHandler();
        const from = moment.tz(
            `${moment(selectedDate).format('YYYY-MM-DD')} ${props.from.format('LT').toUpperCase()}`,
            'YYYY-MM-DD hh:mm A',
            marketplace.schedule.timeZone
        );
        const nights = Math.max(0, moment(props.to).startOf('day').diff(moment(from).startOf('day'), 'days'));
        props.onSelectPeriod(from, props.to, nights);
    };

    const selectCheckOutHandler = (selectedDate: Date) => {
        closeCheckOutHandler();
        const to = moment.tz(
            `${moment(selectedDate).format('YYYY-MM-DD')} ${props.to.format('LT').toUpperCase()}`,
            'YYYY-MM-DD hh:mm A',
            marketplace.schedule.timeZone
        );
        const nights = Math.max(0, moment(to).startOf('day').diff(moment(props.from).startOf('day'), 'days'));
        props.onSelectPeriod(props.from, to, nights);
    };

    useEffect(() => {
        ref?.current?.scrollIntoView();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, props.readOnly]);

    return (
        <div className={clsx(styles.container, props.className)}>
            <div className={styles.sectionContainer}>
                <div
                    className={clsx(styles.sectionCell, {
                        [styles.marginBottom]: props.className
                    })}
                >
                    {
                        <div
                            ref={ref}
                            className={styles.titleContainer}
                        >
                            <span className={styles.title}>{props.labelTimeStart}</span>
                        </div>
                    }
                    <div className={styles.sectionCalendar}>
                        <span className={styles.text}>{props.from.format('ddd, DD MMM YYYY')}</span>
                        <DateSelectorComponent
                            id="checkin"
                            expanded={expanded.checkIn}
                            className={styles.contentCheckIn}
                            onExpand={expandCheckInHandler}
                            onClickAway={closeCheckInHandler}
                        >
                            <CalendarBooking
                                date={props.from}
                                onDaySelect={selectCheckInHandler}
                            />
                        </DateSelectorComponent>
                    </div>
                </div>
                <div style={{ display: 'flex' }}>
                    <div
                        className={clsx(styles.sectionCell, {
                            [styles.marginBottom]: props.className
                        })}
                    >
                        <div className={styles.titleContainer}>
                            <span className={styles.title}>{props.labelTimeEnd}</span>
                        </div>
                        <div className={styles.section}>
                            <span className={styles.text}>{props.to.format('ddd, DD MMM YYYY')}</span>
                            <DateSelectorComponent
                                id="checkout"
                                expanded={expanded.checkOut}
                                className={styles.contentCheckOut}
                                onExpand={expandCheckOutHandler}
                                onClickAway={closeCheckOutHandler}
                            >
                                <CalendarBooking
                                    date={props.to}
                                    onDaySelect={selectCheckOutHandler}
                                />
                            </DateSelectorComponent>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: 'flex' }}>
                <div
                    className={clsx(styles.sectionCell, {
                        [styles.marginBottom]: props.className
                    })}
                >
                    <div className={styles.sectionNights}>
                        <div className={styles.titleContainer}>
                            <span className={styles.title}>Nights</span>
                        </div>
                        <div className={styles.sectionNight}>
                            <FontAwesomeIcon
                                icon={faMoon}
                                className={styles.iconNights}
                            />
                            <span className={styles.title}>{`${Math.max(
                                0,
                                moment(props.to).startOf('day').diff(moment(props.from).startOf('day'), 'days')
                            )} Nights`}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PeriodSelectorSection;
